
import { fn } from '../utils';
import { AsObservableArray } from '../navkendo/kutils';
import { IPageObj, TDefaultPageObj, IPageMetaData, IPageQueryConstraint } from '../nav/navtypes';
import { getOrCreateKendoModel, IObservableArray } from './navmodel';
import { NavData, IsNavPageItem, RegisterIndirection } from './indirection';
import { NavPropertyItems } from './navpropertyitems';
import { NavPageItem } from './navpageitem';
import { NavPropertyItem } from './navpropertyitem';
import { NavClientService } from './navclientservice';
import { INavPageClientInfo } from './navmodel';

/** Container of Page Items Synced via ReadMultiple/UpdateMultiple */
export class NavPageItems<TPage extends IPageObj = TDefaultPageObj> extends NavPropertyItems<TPage> {
    public readonly IsNew = false;
    public Container: NavPageItems;
    /** Current Item for Execution */
    public Current: NavPageItem;
    public readonly MetaData: IPageMetaData<TPage>;
    public readonly ModelItems: IObservableArray<TPage>;
    public readonly Items: Array<NavPageItem<TPage>>;
    constructor(public readonly Service: NavClientService, public ClientInfo: INavPageClientInfo<TPage>,
        public rawDataItems: any[], public readonly Params: IPageQueryConstraint[], public readonly AllRead) {
        super(Service, ClientInfo.MetaData);
        this.Container = this;
        var anchor = this.Anchor = new NavData.NavItemAnchor(Service, this);
        this.Anchor.Source = this as NavPageItems;
        var modelType = getOrCreateKendoModel(ClientInfo.MetaData);
        let Items = this.Items = rawDataItems.map(readItem => {
            return <NavPageItem<TPage>>new NavData.NavPageItem(ClientInfo, Service, <any>new modelType(readItem), fn.clone(readItem), anchor);
        });
        Items.forEach(item => item.Container = this);
        this.MetaData = ClientInfo.MetaData;
        this.ModelItems = AsObservableArray(Items.map(item => item.Model));
        this.PageName = this.MetaData.PageName;
    }
    /** PageName this Item was Loaded From */
    public readonly PageName: string;

    public DeleteItems(ids: string[] | NavPageItem[]) {
        let Service = this.Service,
            toDelete = this.GetItems(ids),
            deleteFunction = this.MetaData.CustomInfo && this.MetaData.CustomInfo.DeleteRowMethod;
        if (deleteFunction)
            doDelete = (x: NavPageItem) => Service.ExecuteMethod(x, { MethodName: deleteFunction }).then(fn.nullOp);
        else
            var doDelete = (x: NavPageItem) => Service.Delete(x, true).then(fn.nullOp);
        return toDelete.reduce((p, c: NavPageItem<TPage>) => {
            return p.then(() => doDelete(c));
        }, Service.$c.when(<any>undefined)).then((s) => {
            let Items = this.Items,
                deleted = Items.filter(item => item.IsDeletedInNav);
            var selIds = fn.except(this.SelectedIds, deleted.map(item => item.ClientKey));
            if (selIds.length == 0) {
                var exists = Items.filter(item => !item.IsDeletedInNav)[0];
                if (exists)
                    selIds.push(exists.ClientKey);
            }
            this.SetSelected(selIds);
            if (this.SelectedIds.length == 0 && Items.length > 0) {
                this.SetSelected(Items[0]);
            }
            deleted.forEach(d => this.NotifyDeleted(d.ClientKey));
        });
    }
    public NotifyDeleted(clientKey: string) {
        var result = super.NotifyDeleted(clientKey);
        this.ModelItems.remove(this.ModelItems[result]);
        return result;
    }
    public NotifyAdded(items: Array<NavPropertyItem<TPage>>) {
        super.NotifyAdded(items);
        this.ModelItems.push(...items.map(item => item.Model));
    }
    public InsertNewItem() {
        let insertMethod = this.MetaData.CustomInfo?.InsertRowMethod,
            Items = this.Items;
        if (!insertMethod)
            return this.Service.$c.reject("InsertRowMethod not defined for " + this.MetaData.PageName);
        var currKeys = Items.map(item => item.ClientKey);
        return this.Service.ExecuteMethod(this, { MethodName: insertMethod }).then(r => {
            return Items.filter(item => currKeys.indexOf(item.ClientKey) < 0)[0];
        });
    }

    public DuplicateItems(ids: string[]): PromiseLike<Array<NavPropertyItem<TPage>>> {
        let duplicateMethod = this.MetaData.CustomInfo.DuplicateRowMethod,
            toDuplicate = <Array<NavPageItem<TPage>>>this.GetItems(ids),
            Service = this.Service,
            currKeys = this.Items.map(item => item.ClientKey),
            sequentialDuplicateItems = toDuplicate.reduce((previous, current) =>
                previous.then(() => Service.ExecuteMethod(current, { MethodName: duplicateMethod })),
                Service.$c.when<any>(0));

        return sequentialDuplicateItems.then(r => this.Items.filter(item => currKeys.indexOf(item.ClientKey) < 0));
    }

    public RevertItem(force?: boolean, ignoreIfDeleted?: boolean): PromiseLike<any> {
        if (!force) {
            // if not forced : only when changes are pending
            var changed = this.Items.filter(item => item.GetCurrentServerChanges(true));
            if (changed.length == 0)
                return this.Service.$c.when();
        }
        return this.Service.LoadPageItems<TPage>({ Constraints: <any>this.Params, PageCount: 0, PageName: this.MetaData.PageName, InstanceID : this.Anchor?.InstanceID }, this.ClientInfo, this.ClientInfo.FromStaticDescription)
            .then(info => {
                this.Sync(info.Items);
            });
    }

    /** Sync Key Values only from Reloaded Object from Server */
    SyncKeys(lItems: IPageObj | IPageObj[]) {
        if (!Array.isArray(lItems))
            throw new Error("Expected Array");
        var newKeys = fn.ToDictionaryEx(lItems, l => this.GetKey(l), l => l);
        this.Items.forEach(v => {
            var x = newKeys[v.ClientKey];
            if (x) {
                v.SyncKeys(x);
            };
        });
    }

    /** Apply State from Reloaded Object from Server */
    Sync(lItems: NavPageItem<TPage> | Array<NavPageItem<TPage>>) {
        if (IsNavPageItem<TPage>(lItems))
            throw new Error("Expected Array");

        const infos = fn.OuterJoin(this.Items, lItems, pItem => pItem.ClientKey, (exItem, cItem) => {
            return { Current: exItem, ToSync: cItem };
        });
        fn.sort(infos, item => (!item.ToSync ? /*Delete : last*/3 : (!item.Current ? /** Add */2 : 1)));
        infos.forEach(info => {
            if (!info.ToSync) {
                // Delete
                info.Current.IsDeletedInNav = true;
                this.NotifyDeleted(info.Current.ClientKey);
            } else if (!info.Current) {
                // Add
                const ToSync = info.ToSync as NavPageItem;
                ToSync.Container = this;
                ToSync.Anchor = this.Anchor;
                this.NotifyAdded([ToSync]);
            } else {
                // Change
                info.Current.Sync(info.ToSync);
            }
        });
    }
}
RegisterIndirection("NavPageItems", NavPageItems);
